/* eslint-disable brace-style */
import { useState, useContext, useRef, useMemo } from 'react';
import { currentUtcMillis, getDateInCustomTimezone, utcDateToMillis } from 'utils/dateManagement';
import { Context as AuthContext } from 'contexts/auth';
import { Context as AlertContext } from 'contexts/alert';
import { useAggregate } from 'hooks';
import { Config } from 'configs';
import { useLocalStorage, useSessionStorage } from 'hooks/useLocalStorage';
import { extractRecordsFromAggreagte } from 'utils/renderingHelpers';
import { useInterval } from 'hooks/useInterval';

export const useOrders = () => {
  const { state: auth } = useContext(AuthContext);
  const { alertController } = useContext(AlertContext);

  const [currentClosedPage, setCurrentClosedPage] = useState(1);
  const [filterSnapshot, setFilterSnapshot] = useState(null);

  const [filterFromDate, setFilterFromDate, filterFromDateRef] = useLocalStorage(`orders-filter-from-${auth.company?.company}`, '');
  const [filterToDate, setFilterToDate, filterToDateRef] = useLocalStorage(`orders-filter-to-${auth.company?.company}`, '');
  const [filterquickDateOption, setFilterQuickDateOption, filterQuickDateOptionRef] = useLocalStorage(`orders-filter-quick-date-${auth.company?.company}`, 7);
  const [filterText, setFilterText, filterTextRef] = useLocalStorage(`orders-filter-search-${auth.company?.company}`, '');
  const [filterTable, setFilterTable, filterTableRef] = useLocalStorage(`orders-filter-table-${auth.company?.company}`, '');
  const [filterType, setFilterType, filterTypeRef] = useSessionStorage(`orders-filter-type-${auth.company?.company}`, '');
  // const [filterEmployee, setFilterEmployee, filterEmployeeRef] = useLocalStorage(`orders-filter-employee-${auth.company?.company}`, '');
  const [filterRestaurant, setFilterRestaurant, filterRestaurantRef] = useLocalStorage(`orders-filter-restaurant-${auth.company?.company}`, '');

  const [sort, setSort] = useLocalStorage(`orders-sort-${auth.company?.company}`, { field: 'order_number', dbField: 'order_number', order: -1 });
  const changedSort = useRef({ value: null }).current;

  // orderId: { ... }
  const previousOrdersData = useRef({ value: {} }).current;

  const buildFuilter = () => {
    const filter = { companyId: auth.company?.company };

    const filterSnapshotTemp = {};

    if (filterTypeRef.value === 'delivery') {
      filter.typeFilter = '{ $or: [ { $eq: [ "$is_delivery", true ] }, { $eq: [ "$is_takeout", true ] } ] }';
    } else if (filterTypeRef.value === 'dineIn') {
      filter.typeFilter = '{ $and: [ { $ne: [ "$is_delivery", true ] }, { $ne: [ "$is_takeout", true ] } ] }';
    } else {
      filter.typeFilter = {};
    }

    if (filterTableRef.value) {
      filter.tableFilter = `{ $eq: [ "$table", ObjectId('${filterTableRef.value}') ] }`;
    } else {
      filter.tableFilter = {};
    }

    let fromDateMillis;
    let toDateMillis;

    // Today
    if (filterQuickDateOptionRef.value === 0) {
      fromDateMillis = utcDateToMillis(`${getDateInCustomTimezone(currentUtcMillis())}T00:00:00Z`);
      toDateMillis = currentUtcMillis() + 10 * 3600000; // +10h
    }
    // Last 7 days
    else if (filterQuickDateOptionRef.value === 7) {
      fromDateMillis = utcDateToMillis(`${getDateInCustomTimezone(currentUtcMillis() - 7 * 24 * 3600000)}T00:00:00Z`);
      toDateMillis = currentUtcMillis() + 10 * 3600000; // +10h
    }
    // Last 30 days
    else if (filterQuickDateOptionRef.value === 30) {
      fromDateMillis = utcDateToMillis(`${getDateInCustomTimezone(currentUtcMillis() - 30 * 24 * 3600000)}T00:00:00Z`);
      toDateMillis = currentUtcMillis() + 10 * 3600000; // +10h
    }
    // Custom date
    else if (filterQuickDateOptionRef.value === -1) {
      fromDateMillis = utcDateToMillis(filterFromDateRef.value);
      toDateMillis = utcDateToMillis(filterToDateRef.value ? `${filterToDateRef.value}T23:59:59Z` : null);
    }

    if (fromDateMillis && toDateMillis) {
      filter.dateFilter = `{ $or: [
        { $and: [ { $gte: [ "$date_utc", ${fromDateMillis} ] },{ $lte: [ "$date_utc", ${toDateMillis} ] } ] },
        { $and: [ { $gte: [ "$delivery_date", ${fromDateMillis} ] },{ $lte: [ "$delivery_date", ${toDateMillis} ] } ] },
        { $and: [ { $gte: [ "$prepare_date", ${fromDateMillis} ] },{ $lte: [ "$prepare_date", ${toDateMillis} ] } ] },
      ] }`;
    } else if (fromDateMillis) {
      filter.dateFilter = `{ $or: [
        { $gte: [ "$date_utc", ${fromDateMillis} ] },
        { $gte: [ "$delivery_date", ${fromDateMillis} ] },
        { $gte: [ "$prepare_date", ${fromDateMillis} ] },
      ] }`;
    } else if (toDateMillis) {
      filter.dateFilter = `{ $or: [
        { $lte: [ "$date_utc", ${toDateMillis} ] },
        { $lte: [ "$delivery_date", ${toDateMillis} ] },
        { $lte: [ "$prepare_date", ${toDateMillis} ] },
      ] }`;
    } else {
      filter.dateFilter = {};
    }

    /*
    if (fromDateMillis && toDateMillis) {
      filter.dateFilter = `{ $gte: [ "$date_utc", ${fromDateMillis} ] },{ $lte: [ "$date_utc", ${toDateMillis} ] }`;
    } else if (fromDateMillis) {
      filter.dateFilter = `{ $gte: [ "$date_utc", ${fromDateMillis} ] }`;
    } else if (toDateMillis) {
      filter.dateFilter = `{ $lte: [ "$date_utc", ${toDateMillis} ] }`;
    } else {
      filter.dateFilter = {};
    } */

    if (filterRestaurantRef.value) {
      filter.restaurantFilter = `{ _id: { $eq: ObjectId('${filterRestaurantRef.value}') } }`;
    } else {
      filter.restaurantFilter = {};
    }

    const parsedOrderNumber = parseInt(filterTextRef.value, 10);

    if (parsedOrderNumber) {
      filter.numberFilter = `{ $eq: [ "$number", ${parsedOrderNumber} ] }`;
    } else {
      filter.numberFilter = {};
    }

    const sortKey = changedSort.value?.dbField || sort?.dbField || 'order_number';

    if (Array.isArray(sortKey)) {
      filter.sort = {};
      sortKey.forEach((key) => {
        filter.sort[`'${key}'`] = changedSort.value?.order || sort?.order || -1;
      });
    } else {
      filter.sort = { [`${sortKey}`]: changedSort.value?.order || sort?.order || -1 };
    }

    filterSnapshotTemp.fromDate = filterFromDateRef.value;
    filterSnapshotTemp.toDate = filterToDateRef.value;
    filterSnapshotTemp.table = filterTableRef.value;
    filterSnapshotTemp.restaurant = filterRestaurantRef.value;
    filterSnapshotTemp.number = filterTextRef.value;
    filterSnapshotTemp.quickDateOption = filterQuickDateOptionRef.value;
    setFilterSnapshot(filterSnapshotTemp);

    return filter;
  };

  // 1. Get New/Progress/Ready (No pagination)
  const requestProcessingOrdersData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_IN_PROGRESS_ORDERS,
    secretKey: auth.token,
    filterBuilder: () => {
      return buildFuilter();
    },
    loadOnInit: true,
    aggregateIdV2: 'AdminGetOrdersInProgress',
    companyIdForV2: auth.company?.company,
  };

  // 2. Get Completed/Canceled (With Pagination)
  const requestClosedOrdersData = {
    collectionName: Config.DATA.COLLECTIONS.RESTAURANTS,
    aggregateId: Config.DATA.AGGREGATES.GET_ORDERS,
    secretKey: auth.token,
    filterBuilder: () => {
      return buildFuilter();
    },
    pageSize: Config.DEFAULTS.PAGE_SIZE,
    pageNumber: currentClosedPage - 1,
    loadOnInit: true,
    aggregateIdV2: 'AdminGetOrders',
    companyIdForV2: auth.company?.company,
  };

  const [fetchProcessingOrders, ordersProcessingResponse, isProcessingLoading] = useAggregate(requestProcessingOrdersData);
  const [fetchClosedOrders, ordersClosedResponse, isClosedLoading] = useAggregate(requestClosedOrdersData);

  const { paginatedResults: processingOrders } = useMemo(() => {
    const aggregateExtracted = extractRecordsFromAggreagte(ordersProcessingResponse);

    if (aggregateExtracted && aggregateExtracted.paginatedResults) {
      aggregateExtracted.paginatedResults.forEach((item) => {
        // If pickup date changes too much, show alert
        if (previousOrdersData.value[item._id]) {
          const prevItem = previousOrdersData.value[item._id];

          const prepareDateDelta = Math.abs((prevItem.prepare_date || 0) - (item.prepare_date || 0));
          const currentMillisDelta = Math.abs(currentUtcMillis() - (item.date_utc || 0));

          // If at least 3 minutes passed after order placed, and difference in time is more than 3 minutes
          if (currentMillisDelta > 120000 && prepareDateDelta >= 180000) {
            alertController.actionWarning({ text: `Changed order #${item.number} pickup date. Please check the updated date.` });
          }
        }

        previousOrdersData.value[item._id] = { prepare_date: item.prepare_date };
      });
    }

    return aggregateExtracted;
  }, [ordersProcessingResponse]);
  const { paginatedResults: closedOrders, closedTotalCount } = useMemo(() => {
    return extractRecordsFromAggreagte(ordersClosedResponse);
  }, [ordersClosedResponse]);

  const onChangeClosedPage = async (pageNumber) => {
    const request = { ...requestClosedOrdersData, pageNumber: pageNumber - 1 };

    await fetchClosedOrders(false, request);
    setCurrentClosedPage(pageNumber);
  };

  useInterval(
    () => {
      fetchProcessingOrders(true);
      fetchClosedOrders(true);
    },
    15000,
    false,
  );

  return {
    filterFromDate,
    setFilterFromDate,
    filterToDate,
    setFilterToDate,
    filterquickDateOption,
    setFilterQuickDateOption,
    filterText,
    setFilterText,
    filterTable,
    setFilterTable,
    filterType,
    setFilterType,
    filterRestaurant,
    setFilterRestaurant,
    sort,
    setSort,
    changedSort,
    fetchProcessingOrders,
    processingOrders,
    isProcessingLoading,
    fetchClosedOrders,
    closedOrders,
    closedTotalCount,
    isClosedLoading,
    currentClosedPage,
    onChangeClosedPage,
    filterSnapshot,
  };
};
